/**
 * AIScribeConsentModal — shown before every AI Scribe recording or upload.
 *
 * DPDPA 2023 requires explicit informed consent before health-related audio
 * is sent to a third-party processor (Google Gemini). This modal surfaces
 * that consent gate at the point of action, every session.
 */

import React from "react";

interface Props {
  isOpen: boolean;
  onConsent: () => void;
  onDecline: () => void;
}

const AIScribeConsentModal: React.FC<Props> = ({ isOpen, onConsent, onDecline }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <span className="text-xl">🎙️</span>
          <h2 className="text-base font-semibold text-slate-800">AI Scribe — Consent Required</h2>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            To generate the SOAP note and prescription, this consultation's audio will be sent to{" "}
            <span className="font-medium text-slate-900">Google Gemini AI</span> for transcription
            and analysis.
          </p>

          <ul className="text-xs text-slate-500 space-y-1.5 pl-4 list-disc">
            <li>Audio is processed by Google Gemini (third-party AI service)</li>
            <li>No audio is stored permanently on Kaagazy servers</li>
            <li>Ensure patient verbal consent is obtained before recording</li>
          </ul>

          <p className="text-xs text-slate-400">
            By proceeding you confirm informed consent as required under DPDPA 2023.
          </p>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onDecline}
              className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50"
            >
              Decline
            </button>
            <button
              type="button"
              onClick={onConsent}
              className="flex-1 bg-violet-700 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-violet-800"
            >
              I Consent — Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIScribeConsentModal;
